import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { formatDisplayDate } from "../../../../common/utils/date";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import LinkSvg from "../../../../common/svgs/LinkSvg";
import EditSvg from "../../../../common/svgs/EditSvg";
import calendarIcon from "../../../new-reports/assets/calendarIcon.png";
import PanelSummaryCard from "../common/PanelSummaryCard";
import PrescriptionRationale from "./PrescriptionRationale";
import InfoSvg from "../../../../common/svgs/InfoSvg";
import LabPanelStatusBanner from "../common/LabPanelStatusBanner";
import {
  resolveLabPanelStatus,
  getStatusBadgeStyle,
  getStatusIconColor,
  isLabPanelEditable,
  labPanelHasResults,
} from "../../utils/labPanelStatus";

interface LabPanel {
  id?: string;
  userLabPanelID?: string;
  name?: string;
  provider?: string;
  kind?: number;
  startDate?: string;
  startOffset?: number;
  startOffsetUnit?: number;
  items?: { biomarkerid: string; providerbiomarkerid: string }[];
  logicName?: string;
  logicBuilderID?: string;
  system?: number;
  isExpired?: boolean;
  status?: number;
  category?: number;
  serviceType?: number;
  active?: number;
  isActive?: boolean;
}

interface PrescriptionLabPanelCardProps {
  labPanel: LabPanel;
  onEditLabPanel: (labPanel: LabPanel) => void;
  // Client the prescription belongs to — needed by the status banner's
  // authorize/retry flow (basic-info fetch + profile modal).
  customerID: string;
  // Refetch the prescription after a finalize so the panel's status updates.
  onRefetch?: () => void | Promise<void>;
  // Hide the edit pencil and the status banner's actions (History's read-only
  // scopes have no client workspace to act in).
  readOnly?: boolean;
}

const PrescriptionLabPanelCard: React.FC<PrescriptionLabPanelCardProps> = ({
  labPanel,
  onEditLabPanel,
  customerID,
  onRefetch,
  readOnly = false,
}) => {
  const theme: Theme = useTheme();
  const panelName = labPanel.name || "Lab Panel";
  const resolvedStatus = resolveLabPanelStatus(labPanel);
  // Editable only before payment is captured (authorize/payment-failed); any
  // later status locks it. Same rule the backend uses to keep panels on delete.
  const canEditLabPanel = !readOnly && isLabPanelEditable(labPanel);
  // Once the lab returns results, show the same results table as the order
  // detail (values/ranges/dates) instead of the ordered-biomarker name list.
  const showResults = labPanelHasResults(labPanel);

  return (
    <div style={styles.labPanelCard(theme)}>
      {/* Header */}
      <div>
        {/* Title row: name + badge + edit icon */}
        <div style={styles.labPanelHeader}>
          <div style={styles.labPanelTitleRow}>
            <div style={styles.labPanelTitle(theme)}>{panelName}</div>
            {resolvedStatus && (
              <div style={getStatusBadgeStyle(resolvedStatus.tone, theme)}>
                {resolvedStatus.actionRequired &&
                  (resolvedStatus.tone === "warning" ||
                    resolvedStatus.tone === "danger") && (
                    <InfoSvg
                      color={getStatusIconColor(resolvedStatus.tone, theme)}
                      size={12}
                    />
                  )}
                {resolvedStatus.label}
              </div>
            )}
            {canEditLabPanel && (
              <div
                onClick={() => onEditLabPanel(labPanel)}
                style={{ cursor: "pointer", height: "16px" }}
              >
                <EditSvg />
              </div>
            )}
          </div>
          <div style={styles.labPanelType(theme)}>Lab panel</div>
        </div>

        {/* Info row: Prescription Period, Logic name, Logic origin */}
        <div style={styles.labPanelInfoRow}>
          <div style={styles.labPanelInfoItem}>
            <div style={styles.labPanelDetailLabel(theme)}>
              Prescription Period
            </div>
            {labPanel.startDate ? (
              <Box sx={styles.labPanelDatePill(theme)}>
                <Box
                  component="img"
                  src={calendarIcon}
                  alt="calendar"
                  sx={styles.calendarIcon}
                />
                <span>{formatDisplayDate(labPanel.startDate)}</span>
              </Box>
            ) : (
              <div style={styles.labPanelValueNull(theme)}>-</div>
            )}
          </div>
          <div style={styles.labPanelInfoItem}>
            <div style={styles.labPanelDetailLabel(theme)}>Logic name</div>
            {labPanel.logicName ? (
              <Link
                style={styles.logicNameLink(theme)}
                to={`/client/services/logic/${labPanel.logicBuilderID}`}
                target="_blank"
              >
                <div style={styles.logicNameIcon}>
                  <LinkSvg />
                </div>
                <p>{labPanel.logicName}</p>
              </Link>
            ) : (
              <div style={styles.labPanelValueNull(theme)}>-</div>
            )}
          </div>
          <div style={styles.labPanelInfoItem}>
            <div style={styles.labPanelDetailLabel(theme)}>Logic origin</div>
            {labPanel.logicName ? (
              <div style={styles.labPanelDetailValue(theme)}>
                {labPanel.system === 1 ? "PNOE" : "Custom"}
              </div>
            ) : (
              <div style={styles.labPanelValueNull(theme)}>-</div>
            )}
          </div>
        </div>
      </div>

      {/* Prescription details */}
      <div style={styles.prescriptionDetails(theme)}>
        {/* Order-status banner + actions (same component as the order detail),
            shown once the panel has been ordered and carries a status. */}
        {resolvedStatus?.message && !readOnly && (
          <div style={styles.statusBanner}>
            <LabPanelStatusBanner
              panel={labPanel}
              customerID={customerID}
              onRefetch={onRefetch}
              // Unanswered order-entry questions are answered in the same
              // edit-panel modal the pencil opens.
              onAnswerQuestions={() => onEditLabPanel(labPanel)}
              marginBottom={0}
              fontSize={12}
            />
          </div>
        )}
        <div style={styles.prescriptionDetailsTitle(theme)}>
          Prescription details
        </div>
        <PanelSummaryCard
          panel={labPanel}
          showResults={showResults}
          showOrderInfo
        />
      </div>

      {/* Rationale information */}
      <PrescriptionRationale rules={(labPanel as any).rules} disableTopMargin />
    </div>
  );
};

export default PrescriptionLabPanelCard;

const styles = {
  labPanelCard: (theme: Theme) => ({
    padding: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  labPanelHeader: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  labPanelTitleRow: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },
  labPanelTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
  }),
  labPanelType: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  labPanelInfoRow: {
    display: "flex",
    gap: "16px",
    marginTop: "16px",
  },
  labPanelInfoItem: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    flex: 1,
  },
  labPanelDetailLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  labPanelDatePill: (theme: Theme) => ({
    marginTop: "4px",
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    backgroundColor: theme.colorTokens.N[30],
    borderRadius: "8px",
    padding: "8px 12px",
    display: "flex",
    gap: "8px",
    alignItems: "center",
    width: "fit-content" as "fit-content",
  }),
  calendarIcon: {
    width: "12px",
    height: "12px",
    objectFit: "contain" as "contain",
  },
  labPanelValueNull: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginTop: "4px",
  }),
  logicNameLink: (theme: Theme) => ({
    display: "flex",
    padding: "8px 0",
    gap: "8px",
    alignItems: "flex-start",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    width: "fit-content" as "fit-content",
    marginTop: "4px",
    textDecoration: "none",
  }),
  logicNameIcon: {
    marginTop: "2px",
  },
  labPanelDetailValue: (theme: Theme) => ({
    marginTop: "4px",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  prescriptionDetails: (theme: Theme) => ({
    paddingTop: "24px",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  statusBanner: {
    marginBottom: "24px",
  },
  prescriptionDetailsTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "22px",
    marginBottom: "16px",
  }),
};
